import React, { useState, useEffect, useRef } from 'react';
import { Investor } from '../../types';
import { FileUpload } from '../ui/FileUpload';
import { ImageUpload } from '../ui/ImageUpload';
import { ChevronDown, Search, CheckCircle, XCircle, Plus, Trash2 } from 'lucide-react';

interface InvestorFormProps {
  investor?: Investor;
  onSubmit: (
    investor: Omit<Investor, 'id' | 'createdAt' | 'totalInvested' | 'propertiesCount'>,
    files?: {
      csf?: File | null;
      idOficial?: File | null;
      kyc?: File | null;
      caratulaBancaria?: File | null;
      actaConstitutiva?: File | null;
    }
  ) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

// Lista de países con códigos más comunes (especialmente América Latina)
const COUNTRIES = [
  { code: '+52', name: 'México', flag: '🇲🇽' },
  { code: '+1', name: 'Estados Unidos', flag: '🇺🇸' },
  { code: '+1', name: 'Canadá', flag: '🇨🇦' },
  { code: '+54', name: 'Argentina', flag: '🇦🇷' },
  { code: '+55', name: 'Brasil', flag: '🇧🇷' },
  { code: '+56', name: 'Chile', flag: '🇨🇱' },
  { code: '+57', name: 'Colombia', flag: '🇨🇴' },
  { code: '+51', name: 'Perú', flag: '🇵🇪' },
  { code: '+593', name: 'Ecuador', flag: '🇪🇨' },
  { code: '+58', name: 'Venezuela', flag: '🇻🇪' },
  { code: '+505', name: 'Nicaragua', flag: '🇳🇮' },
  { code: '+506', name: 'Costa Rica', flag: '🇨🇷' },
  { code: '+507', name: 'Panamá', flag: '🇵🇦' },
  { code: '+502', name: 'Guatemala', flag: '🇬🇹' },
  { code: '+503', name: 'El Salvador', flag: '🇸🇻' },
  { code: '+504', name: 'Honduras', flag: '🇭🇳' },
  { code: '+34', name: 'España', flag: '🇪🇸' },
  { code: '+33', name: 'Francia', flag: '🇫🇷' },
  { code: '+49', name: 'Alemania', flag: '🇩🇪' },
  { code: '+44', name: 'Reino Unido', flag: '🇬🇧' },
].sort((a, b) => a.name.localeCompare(b.name));

export const InvestorForm: React.FC<InvestorFormProps> = ({
  investor,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    name: (investor?.name || '').toUpperCase(),
    email: investor?.email || '',
    phone: investor?.phone || '',
    status: investor?.status || 'active' as 'active' | 'inactive',
    type: investor?.type || 'fisica' as 'fisica' | 'moral',
    // Identificaciones Física
    curp: investor?.curp || '',
    csf: investor?.csf || '',
    certificadoFIEL: investor?.certificadoFIEL || '',
    idOficial: investor?.idOficial || '',
    kyc: investor?.kyc || '',
    caratulaBancaria: investor?.caratulaBancaria || '',
    // Identificaciones Moral
    curpRepLegal: investor?.curpRepLegal || '',
    actaConstitutiva: investor?.actaConstitutiva || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [additionalEmailInputs, setAdditionalEmailInputs] = useState<string[]>(['']);
  const [countryCode, setCountryCode] = useState('+52'); // México por defecto
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const countrySearchInputRef = useRef<HTMLInputElement>(null);
  
  // Estado para archivos File (no base64)
  const [files, setFiles] = useState<{
    csf?: File | null;
    idOficial?: File | null;
    kyc?: File | null;
    caratulaBancaria?: File | null;
    actaConstitutiva?: File | null;
  }>({});

  // Cerrar dropdown cuando se hace click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setIsCountryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (investor) {
      setFormData({
        name: (investor.name || '').toUpperCase(),
        email: investor.email,
        phone: investor.phone,
        status: investor.status,
        type: investor.type || 'fisica',
        curp: investor.curp || '',
        csf: investor.csf || '',
        certificadoFIEL: investor.certificadoFIEL || '',
        idOficial: investor.idOficial || '',
        kyc: investor.kyc || '',
        caratulaBancaria: investor.caratulaBancaria || '',
        curpRepLegal: investor.curpRepLegal || '',
        actaConstitutiva: investor.actaConstitutiva || '',
      });
      setAdditionalEmailInputs(
        investor.additionalEmails?.length
          ? [...investor.additionalEmails]
          : [''],
      );

      // Extraer código de país del teléfono existente
      const phoneMatch = investor.phone.match(/^(\+\d{1,4})/);
      if (phoneMatch) {
        setCountryCode(phoneMatch[1]);
      }
    } else {
      // Si es nuevo, extraer código de país si ya hay uno en el teléfono
      const phoneMatch = formData.phone.match(/^(\+\d{1,4})/);
      if (phoneMatch) {
        setCountryCode(phoneMatch[1]);
      }
    }
  }, [investor]);

  // Cerrar dropdown cuando se hace click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setIsCountryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }
    
    // Validación de email más robusta
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else {
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = 'El formato del email no es válido. Ejemplo: nombre@dominio.com';
      }
    }
    
    // Validación de teléfono: código de país (+XX) + 10 dígitos
    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es requerido';
    } else {
      // Remover espacios y guiones para validar
      const cleanPhone = formData.phone.replace(/[\s-]/g, '');
      // Patrón: debe empezar con +, tener código de país (1-4 dígitos) y luego exactamente 10 dígitos
      const phoneRegex = /^\+\d{1,4}\d{10}$/;
      if (!phoneRegex.test(cleanPhone)) {
        newErrors.phone = 'El teléfono debe tener código de país (+XX) seguido de 10 dígitos. Ejemplo: +52 123 456 7890';
      }
    }

    // Persona moral: acta constitutiva requerida (archivo o URL existente)
    if (formData.type === 'moral') {
      const hasExisting = !!String(formData.actaConstitutiva || '').trim();
      const hasNewFile = !!files.actaConstitutiva;
      if (!hasExisting && !hasNewFile) {
        newErrors.actaConstitutiva = 'El acta constitutiva es requerida para persona moral';
      }
    }

    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    const primary = formData.email.trim().toLowerCase();
    const seenAdditional = new Set<string>();
    additionalEmailInputs.forEach((raw, index) => {
      const e = raw.trim().toLowerCase();
      if (!e) return;
      if (!emailRegex.test(e)) {
        newErrors[`additionalEmail_${index}`] = 'Correo adicional no válido';
        return;
      }
      if (e === primary) {
        newErrors[`additionalEmail_${index}`] =
          'No puede ser igual al correo principal';
        return;
      }
      if (seenAdditional.has(e)) {
        newErrors[`additionalEmail_${index}`] = 'Correo duplicado';
        return;
      }
      seenAdditional.add(e);
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const collectAdditionalEmails = (): string[] => {
    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    const primary = formData.email.trim().toLowerCase();
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of additionalEmailInputs) {
      const e = raw.trim().toLowerCase();
      if (!e || !emailRegex.test(e) || e === primary || seen.has(e)) continue;
      seen.add(e);
      out.push(e);
    }
    return out;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      // Filtrar archivos que no son null/undefined
      const filesToSubmit = Object.keys(files).reduce((acc, key) => {
        if (files[key as keyof typeof files] !== null && files[key as keyof typeof files] !== undefined) {
          acc[key as keyof typeof files] = files[key as keyof typeof files];
        }
        return acc;
      }, {} as typeof files);

      onSubmit(
        { ...formData, additionalEmails: collectAdditionalEmails() },
        Object.keys(filesToSubmit).length > 0 ? filesToSubmit : undefined,
      );
    }
  };

  const formatPhoneNumber = (code: string, digits: string): string => {
    // Limitar a 10 dígitos
    const limitedDigits = digits.slice(0, 10);
    
    // Formatear los dígitos: 123 456 7890
    let formatted = code;
    if (limitedDigits.length > 0) {
      formatted += ' ' + limitedDigits.slice(0, 3);
      if (limitedDigits.length > 3) {
        formatted += ' ' + limitedDigits.slice(3, 6);
      }
      if (limitedDigits.length > 6) {
        formatted += ' ' + limitedDigits.slice(6, 10);
      }
    }
    
    return formatted;
  };

  const handleCountrySelect = (code: string) => {
    setCountryCode(code);
    setIsCountryDropdownOpen(false);
    setCountrySearchTerm('');
    
    // Obtener los dígitos actuales del teléfono (sin el código de país)
    const currentDigits = formData.phone.replace(/^\+\d{1,4}\s*/, '').replace(/\s/g, '');
    const newPhone = formatPhoneNumber(code, currentDigits);
    
    setFormData((prev) => ({
      ...prev,
      phone: newPhone,
    }));
  };

  // Filtrar países basado en el término de búsqueda
  const filteredCountries = COUNTRIES.filter((country) => {
    const search = countrySearchTerm.toLowerCase();
    return (
      country.name.toLowerCase().includes(search) ||
      country.code.includes(search) ||
      country.flag.includes(search)
    );
  });

  const handlePhoneChange = (value: string) => {
    // Si el usuario borra todo, mantener solo el código de país
    if (!value || value === countryCode) {
      setFormData((prev) => ({
        ...prev,
        phone: countryCode,
      }));
      return;
    }

    // Si el valor no empieza con el código de país actual, intentar detectar nuevo código
    if (!value.startsWith(countryCode)) {
      const match = value.match(/^(\+\d{1,4})/);
      if (match && match[1] !== countryCode) {
        setCountryCode(match[1]);
        const digits = value.replace(/^\+\d{1,4}\s*/, '').replace(/\s/g, '');
        const formatted = formatPhoneNumber(match[1], digits);
        setFormData((prev) => ({
          ...prev,
          phone: formatted,
        }));
        return;
      }
    }

    // Extraer solo los dígitos (sin el código de país y espacios)
    const digits = value.replace(countryCode, '').replace(/\s/g, '').replace(/[^\d]/g, '');
    const formatted = formatPhoneNumber(countryCode, digits);
    
    setFormData((prev) => ({
      ...prev,
      phone: formatted,
    }));
  };

  const handleChange = (field: string, value: string) => {
    if (field === 'phone') {
      handlePhoneChange(value);
    } else if (field === 'name') {
      // Forzar nombre en MAYÚSCULAS (requerimiento)
      const nextValue = (value ?? '').toUpperCase();
      setFormData((prev) => ({
        ...prev,
        [field]: nextValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
    
    // Limpiar error del campo cuando se modifica
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Funciones para validar campos en tiempo real
  const isEmailValid = (): boolean => {
    if (!formData.email.trim()) return false;
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(formData.email.trim()) && !errors.email;
  };

  const isEmailInvalid = (): boolean => {
    if (!formData.email.trim()) return false;
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return !emailRegex.test(formData.email.trim()) || !!errors.email;
  };

  const isPhoneValid = (): boolean => {
    if (!formData.phone.trim()) return false;
    const cleanPhone = formData.phone.replace(/[\s-]/g, '');
    const phoneRegex = /^\+\d{1,4}\d{10}$/;
    return phoneRegex.test(cleanPhone) && !errors.phone;
  };

  const isPhoneInvalid = (): boolean => {
    if (!formData.phone.trim()) return false;
    const cleanPhone = formData.phone.replace(/[\s-]/g, '');
    const phoneRegex = /^\+\d{1,4}\d{10}$/;
    return !phoneRegex.test(cleanPhone) || !!errors.phone;
  };

  // Validación de CURP (formato mexicano: 18 caracteres)
  // Formato: 4 letras + 6 dígitos + 1 letra (H/M) + 2 letras + 3 caracteres + 2 caracteres
  const isCURPValid = (): boolean => {
    if (!formData.curp || !formData.curp.trim()) return false;
    const curp = formData.curp.trim().toUpperCase();
    // Regex: 4 letras + 6 dígitos + H o M + 2 letras + 3 alfanuméricos + 2 alfanuméricos
    const curpRegex = /^[A-Z]{4}[0-9]{6}[HM][A-Z]{2}[0-9A-Z]{3}[0-9A-Z]{2}$/;
    return curpRegex.test(curp) && curp.length === 18;
  };

  const isCURPInvalid = (): boolean => {
    if (!formData.curp || !formData.curp.trim()) return false;
    const curp = formData.curp.trim().toUpperCase();
    const curpRegex = /^[A-Z]{4}[0-9]{6}[HM][A-Z]{2}[0-9A-Z]{3}[0-9A-Z]{2}$/;
    return !curpRegex.test(curp) || curp.length !== 18;
  };

  const isFisica = formData.type === 'fisica';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Nombre */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nombre <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 uppercase ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Ej: Juan Pérez"
          />
          {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 ${
                isEmailInvalid() ? 'border-red-500 pr-10' : isEmailValid() ? 'border-green-500 pr-10' : 'border-gray-300'
              }`}
              placeholder="Ej: juan.perez@dominio.com"
            />
            {isEmailValid() && (
              <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
            )}
            {isEmailInvalid() && (
              <XCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
            )}
          </div>
          {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
          <p className="mt-1 text-xs text-gray-500">
            Correo de acceso al portal y notificaciones principales.
          </p>
        </div>

        {/* Correos adicionales */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Correos adicionales (opcional)
          </label>
          <p className="text-xs text-gray-500 mb-3">
            También recibirán avisos (por ejemplo, estados de cuenta). No crean cuenta en el
            portal.
          </p>
          <div className="space-y-2">
            {additionalEmailInputs.map((value, index) => (
              <div key={index} className="flex gap-2 items-start">
                <input
                  type="email"
                  value={value}
                  onChange={(e) => {
                    const next = [...additionalEmailInputs];
                    next[index] = e.target.value;
                    setAdditionalEmailInputs(next);
                    const errKey = `additionalEmail_${index}`;
                    if (errors[errKey]) {
                      setErrors((prev) => {
                        const n = { ...prev };
                        delete n[errKey];
                        return n;
                      });
                    }
                  }}
                  className={`flex-1 px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 ${
                    errors[`additionalEmail_${index}`]
                      ? 'border-red-500'
                      : 'border-gray-300'
                  }`}
                  placeholder="Ej: socio@empresa.com"
                  autoComplete="off"
                />
                {additionalEmailInputs.length > 1 && (
                  <button
                    type="button"
                    onClick={() =>
                      setAdditionalEmailInputs(
                        additionalEmailInputs.filter((_, i) => i !== index),
                      )
                    }
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg border border-gray-200"
                    title="Quitar correo"
                    aria-label="Quitar correo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {additionalEmailInputs.map(
              (_, index) =>
                errors[`additionalEmail_${index}`] && (
                  <p key={`err-${index}`} className="text-sm text-red-500">
                    {errors[`additionalEmail_${index}`]}
                  </p>
                ),
            )}
          </div>
          <button
            type="button"
            onClick={() => setAdditionalEmailInputs([...additionalEmailInputs, ''])}
            className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80"
          >
            <Plus className="w-4 h-4" />
            Agregar otro correo
          </button>
        </div>

        {/* Teléfono */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Teléfono <span className="text-red-500">*</span>
          </label>
          <div className="flex">
            {/* Selector de código de país */}
            <div className="relative" ref={countryDropdownRef}>
              <button
                type="button"
                onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                className={`px-3 py-2 bg-white border rounded-l-lg focus:outline-none focus:ring-2 focus:ring-primary flex items-center space-x-1 ${
                  errors.phone ? 'border-red-500 border-r-0' : 'border-gray-300 border-r-0'
                }`}
              >
                <span className="text-base">{COUNTRIES.find(c => c.code === countryCode)?.flag || '🇲🇽'}</span>
                <span className="text-sm font-medium text-gray-700">{countryCode}</span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isCountryDropdownOpen ? 'transform rotate-180' : ''}`} />
              </button>

              {/* Dropdown de países */}
              {isCountryDropdownOpen && (
                <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-300 rounded-lg shadow-lg">
                  {/* Buscador */}
                  <div className="p-2 border-b border-gray-200">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        ref={countrySearchInputRef}
                        type="text"
                        value={countrySearchTerm}
                        onChange={(e) => setCountrySearchTerm(e.target.value)}
                        placeholder="Buscar país..."
                        className="w-full pl-8 pr-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  
                  {/* Lista de países */}
                  <div className="max-h-64 overflow-auto">
                    {filteredCountries.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500 text-center">
                        No se encontraron países
                      </div>
                    ) : (
                      filteredCountries.map((country, index) => (
                        <button
                          key={`${country.code}-${country.name}-${index}`}
                          type="button"
                          onClick={() => handleCountrySelect(country.code)}
                          className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-3 ${
                            country.code === countryCode ? 'bg-primary/10' : ''
                          }`}
                        >
                          <span className="text-base">{country.flag}</span>
                          <span className="text-sm font-medium text-gray-700">{country.code}</span>
                          <span className="text-sm text-gray-600">{country.name}</span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Campo de número de teléfono */}
            <div className="relative flex-1">
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-r-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 ${
                  isPhoneInvalid() ? 'border-red-500 border-l-0 pr-10' : isPhoneValid() ? 'border-green-500 border-l-0 pr-10' : 'border-gray-300 border-l-0'
                }`}
                placeholder="123 456 7890"
              />
              {isPhoneValid() && (
                <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
              )}
              {isPhoneInvalid() && (
                <XCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
              )}
            </div>
          </div>
          {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
          {!errors.phone && (
            <p className="mt-1 text-xs text-gray-500">
              Formato: código de país (+XX) + 10 dígitos. Ejemplo: +52 123 456 7890
            </p>
          )}
        </div>

        {/* Tipo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tipo <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.type}
            onChange={(e) => handleChange('type', e.target.value)}
            className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="fisica">Física</option>
            <option value="moral">Moral</option>
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Si es <strong>persona moral</strong>, selecciona “Moral” para poder cargar el <strong>Acta Constitutiva</strong>.
          </p>
        </div>

        {/* Estado - Solo mostrar al editar */}
        {investor && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
            </select>
          </div>
        )}
      </div>

      {/* Identificaciones Oficiales */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Identificaciones Oficiales
        </h3>

        {isFisica ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CURP
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.curp}
                    onChange={(e) => handleChange('curp', e.target.value)}
                    className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 uppercase ${
                      isCURPInvalid() ? 'border-red-500 pr-10' : isCURPValid() ? 'border-green-500 pr-10' : 'border-gray-300'
                    }`}
                    placeholder="Ej: ABCD123456HDF789GH"
                    maxLength={18}
                  />
                  {isCURPValid() && (
                    <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
                  )}
                  {isCURPInvalid() && (
                    <XCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
                  )}
                </div>
                {formData.curp && !isCURPValid() && (
                  <p className="mt-1 text-xs text-gray-500">
                    Formato: 18 caracteres (4 letras + 6 dígitos + H/M + 2 letras + 3 caracteres + 2 caracteres)
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Certificado FIEL
                </label>
                <input
                  type="text"
                  value={formData.certificadoFIEL}
                  onChange={(e) => handleChange('certificadoFIEL', e.target.value)}
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
                  placeholder="Ej: 12345678901234567890"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CSF
              </label>
              <FileUpload
                currentFile={formData.csf}
                onFileChange={(file) => {
                  setFiles(prev => ({ ...prev, csf: file }));
                }}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSizeMB={10}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID Oficial
              </label>
              <FileUpload
                currentFile={formData.idOficial}
                onFileChange={(file) => {
                  setFiles(prev => ({ ...prev, idOficial: file }));
                }}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSizeMB={10}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                KYC
              </label>
              <FileUpload
                currentFile={formData.kyc}
                onFileChange={(file) => {
                  setFiles(prev => ({ ...prev, kyc: file }));
                }}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSizeMB={10}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Carátula Bancaria
              </label>
              <FileUpload
                currentFile={formData.caratulaBancaria}
                onFileChange={(file) => {
                  setFiles(prev => ({ ...prev, caratulaBancaria: file }));
                }}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSizeMB={10}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CURP del Representante Legal
                </label>
                <input
                  type="text"
                  value={formData.curpRepLegal}
                  onChange={(e) => handleChange('curpRepLegal', e.target.value)}
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
                  placeholder="Ej: ABC123456DEF789GH"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Certificado FIEL
                </label>
                <input
                  type="text"
                  value={formData.certificadoFIEL}
                  onChange={(e) => handleChange('certificadoFIEL', e.target.value)}
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
                  placeholder="Ej: 12345678901234567890"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CSF
              </label>
              <FileUpload
                currentFile={formData.csf}
                onFileChange={(file) => {
                  setFiles(prev => ({ ...prev, csf: file }));
                }}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSizeMB={10}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID Oficial
              </label>
              <FileUpload
                currentFile={formData.idOficial}
                onFileChange={(file) => {
                  setFiles(prev => ({ ...prev, idOficial: file }));
                }}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSizeMB={10}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                KYC
              </label>
              <FileUpload
                currentFile={formData.kyc}
                onFileChange={(file) => {
                  setFiles(prev => ({ ...prev, kyc: file }));
                }}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSizeMB={10}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Carátula Bancaria
              </label>
              <FileUpload
                currentFile={formData.caratulaBancaria}
                onFileChange={(file) => {
                  setFiles(prev => ({ ...prev, caratulaBancaria: file }));
                }}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSizeMB={10}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Acta Constitutiva
              </label>
              <FileUpload
                currentFile={formData.actaConstitutiva}
                onFileChange={(file) => {
                  setFiles(prev => ({ ...prev, actaConstitutiva: file }));
                }}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSizeMB={10}
              />
              {errors.actaConstitutiva && (
                <p className="mt-1 text-sm text-red-500">{errors.actaConstitutiva}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Botones */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {isLoading && (
            <svg
              className="animate-spin h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          )}
          <span>{investor ? 'Guardar Cambios' : 'Agregar Inversionista'}</span>
        </button>
      </div>
    </form>
  );
};

